import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import gql from 'graphql-tag';
import { useStoreState } from 'easy-peasy';
import apolloClient from '../../../../utils/apolloClient';

const Bar = styled.div`
  background: #fff;
  border: 1px solid #E8DDD6;
  border-radius: 12px;
  padding: 13px 16px;
  margin-bottom: 14px;
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  box-shadow: 0 1px 4px rgba(22, 17, 13, 0.06);
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 220px;
  padding: 8px 12px;
  border: 1.5px solid #E8DDD6;
  border-radius: 8px;
  font-size: 13px;
  color: #16110D;
  background: #fff;
  font-family: 'DM Sans', sans-serif;
  &:focus {
    outline: none;
    border-color: #F36E21;
  }
  &::placeholder {
    color: #B8AAA2;
  }
`;

const SEARCH_PLACES = gql`
  query SearchPlacesForCompanyFilter($input: SearchInput) {
    search_places(input: $input) {
      place_listing {
        place_id
        name
        city
        state
      }
    }
  }
`;

const fetchPlaceSuggestions = async (userId, query) => {
  try {
    const { data } = await apolloClient.clientPrivate.query({
      query: SEARCH_PLACES,
      variables: {
        input: {
          user_id: userId,
          from: 0,
          size: 10,
          filter: { place_filter: { status: 'ACTIVE', place_name: query } },
        },
      },
      fetchPolicy: 'network-only',
    });
    return data?.search_places?.place_listing ?? [];
  } catch {
    return [];
  }
};

const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
    <circle cx="6.5" cy="6.5" r="5" stroke="#B9AFA8" strokeWidth="1.5" />
    <path d="M11 11L14.5 14.5" stroke="#B9AFA8" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const SearchWrapper = styled.div`
  position: relative;
  flex: 1;
  min-width: 220px;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 7px 10px;
  border: 1.5px solid #E8DDD6;
  border-radius: 8px;
  background: #fff;
  &:focus-within {
    border-color: #F36E21;
  }
`;

const PlaceSearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 13px;
  color: #16110D;
  background: transparent;
  font-family: 'DM Sans', sans-serif;
  &::placeholder { color: #B8AAA2; }
`;

const Spinner = styled.div`
  width: 13px;
  height: 13px;
  border: 2px solid #E8DDD6;
  border-top-color: #F36E21;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  flex-shrink: 0;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const ClearBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #B9AFA8;
  font-size: 13px;
  padding: 0;
  line-height: 1;
  flex-shrink: 0;
  &:hover { color: #F36E21; }
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #E8DDD6;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(22,17,13,0.1);
  z-index: 200;
  overflow: hidden;
`;

const DropdownItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 9px 12px;
  cursor: pointer;
  font-size: 13px;
  gap: 8px;
  &:hover { background: #FFF5EF; }
`;

const PlaceName = styled.span`
  color: #16110D;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PlaceMeta = styled.span`
  color: #B9AFA8;
  font-size: 12px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const PlaceSearchWidget = ({ placeIdFilter, setPlaceIdFilter }) => {
  const { userId } = useStoreState(state => state.auth);
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const onClickOutside = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const handleQueryChange = e => {
    const { value } = e.target;
    setInputValue(value);
    if (placeIdFilter) setPlaceIdFilter('');
    clearTimeout(debounceRef.current);
    if (!value.trim()) {
      setSuggestions([]);
      setSearching(false);
      setDropdownOpen(false);
      return;
    }
    setSearching(true);
    setDropdownOpen(true);
    debounceRef.current = setTimeout(async () => {
      const results = await fetchPlaceSuggestions(userId, value);
      setSuggestions(results);
      setSearching(false);
    }, 300);
  };

  const handleSelect = place => {
    setPlaceIdFilter(place.place_id);
    setInputValue(place.name);
    setSuggestions([]);
    setDropdownOpen(false);
  };

  const handleClear = () => {
    setInputValue('');
    setSuggestions([]);
    setDropdownOpen(false);
    setPlaceIdFilter('');
    clearTimeout(debounceRef.current);
  };

  return (
    <SearchWrapper ref={wrapperRef}>
      <SearchBar>
        <SearchIcon />
        <PlaceSearchInput
          placeholder="Search place name…"
          value={inputValue}
          onChange={handleQueryChange}
          autoComplete="off"
        />
        {searching && <Spinner />}
        {!searching && inputValue && (
          <ClearBtn onClick={handleClear}>✕</ClearBtn>
        )}
      </SearchBar>
      {dropdownOpen && (
        <Dropdown>
          {searching && (
            <DropdownItem style={{ color: '#b9afa8', cursor: 'default' }}>Searching…</DropdownItem>
          )}
          {!searching && suggestions.length === 0 && (
            <DropdownItem style={{ color: '#b9afa8', cursor: 'default' }}>No places found</DropdownItem>
          )}
          {!searching && suggestions.map(p => (
            <DropdownItem key={p.place_id} onMouseDown={() => handleSelect(p)}>
              <PlaceName>{p.name}</PlaceName>
              <PlaceMeta>{p.city}, {p.state}</PlaceMeta>
            </DropdownItem>
          ))}
        </Dropdown>
      )}
    </SearchWrapper>
  );
};

const FilterSelect = styled.select`
  width: 150px;
  padding: 8px 12px;
  border: 1.5px solid #E8DDD6;
  border-radius: 8px;
  font-size: 13px;
  color: #16110D;
  background: #fff;
  cursor: pointer;
  font-family: 'DM Sans', sans-serif;
  &:focus {
    outline: 2px solid #F36E21;
    outline-offset: -1px;
  }
`;

const FilterBar = ({
  nameSearch,
  setNameSearch,
  placeIdFilter,
  setPlaceIdFilter,
  statusFilter,
  setStatusFilter,
  stateFilter,
  setStateFilter,
  abnStatusFilter,
  setAbnStatusFilter,
  bankStatusFilter,
  setBankStatusFilter,
}) => (
  <Bar>
    <SearchInput
      placeholder="🔍  Search company name..."
      value={nameSearch}
      onChange={e => setNameSearch(e.target.value)}
    />
    <PlaceSearchWidget placeIdFilter={placeIdFilter} setPlaceIdFilter={setPlaceIdFilter} />
    <FilterSelect value={stateFilter} onChange={e => setStateFilter(e.target.value)}>
      <option value="">All States</option>
      <option value="New South Wales">NSW</option>
      <option value="Victoria">VIC</option>
      <option value="Queensland">QLD</option>
      <option value="Western Australia">WA</option>
      <option value="South Australia">SA</option>
      <option value="Australian Capital Territory">ACT</option>
      <option value="Tasmania">TAS</option>
      <option value="Northern Territory">NT</option>
    </FilterSelect>
    <FilterSelect value={abnStatusFilter} onChange={e => setAbnStatusFilter(e.target.value)}>
      <option value="">All ABN Status</option>
      <option value="AVAILABLE">ABN Verified</option>
      <option value="UNAVAILABLE">Pending ABN</option>
    </FilterSelect>
    <FilterSelect value={bankStatusFilter} onChange={e => setBankStatusFilter(e.target.value)}>
      <option value="">All Bank Status</option>
      <option value="SET">Bank Set</option>
      <option value="NOT_SET">Bank Not Set</option>
    </FilterSelect>
  </Bar>
);

export default FilterBar;
